import { gql } from "graphql-tag";

/**
 * Gets booking using its Id
 * We only get top level fields
 * Here is a variable example
 * { "Id": "(id,eq,1234)" }
 */
export const GET_BookingInfo = gql`
  query GetBookingInfo($id: String!) {
    getBookingInfoById: ProductSlotBookingList(where: $id) {
      id: Id
      uuid: Uuid
      reference: Reference
      state: State
      languageId: LanguageId
      sellerId: SellerId
      userId: UserId
      customerId: CustomerId
      email: Email
      firstName: FirstName
      lastName: LastName
      currency: Currency
      insertDateTime: InsertDateTime
      updatedAt: UpdatedAt
      message: Message
      type: Type

      customFieldsAnswers: BookingFieldAnswerList {
        answer: Answer
        bookingUuid: BookingId
        createdAt: CreatedAt
        CustomBookingFieldId
        customField: CustomBookingFieldRead {
          answerExample: AnswerExample
          cancelled: Canceled
          id: Id
          informationType: InformationType
          list: List
          name: Name
          position: Position
          productId: ProductId
          questionType: QuestionType
          validation: Validation
          visibleFor: VisibleFor
        }
        updatedAt: UpdatedAt
        uuid: Id
        id: Id
      }

      CustomerRead {
        id: Id
        email: Email
        firstName: FirstName
        lastName: LastName
        city: City
        address: Address
        phone: Phone
        zipCode: ZipCode
        allergies: Allergies
        language: LanguageRead {
          id: Id
          name: Name
          code: Code
          nativeName: NativeName
          local: Local
        }
        birthDate: BirthDate
        title: Title
        emailMarketingOrganiser: EmailMarketingOrganiser
        company: Company
        country: CountryRead {
          id: Id
          name: Name
        }
      }

      orderSessions: OrderSessionList {
        Id
        Uid
        State
        CreatedAt
        UpdatedAt
        CompletionDate
        Channel
        seqNumber: SeqNumber
        receiptCounter: Reference
      }

      GroupRead {
        groupUuid: Uuid
      }
    }
  }
`;

/**
 * Fetch PSS list paginated using its bookingId
 * "(booking_id,eq,7256454)"
 */
export const GET_PSSByBookingId = gql`
  query GetPSSByBookingId($id: String!, $skip: Int, $limit: Int) {
    getPSSBookingId: ProductSlotStateList(
      where: $id
      offset: $skip
      limit: $limit
    ) {
      id: Id
      uuid: Uuid
      token: Token
      bookingId: BookingId
      paymentStatus: PaymentStatus
      bookingStatus: BookingStatus
      scanningStatus: ScanningStatus
      scanningUserId: ScanningUserId
      seat: Seat
      fullSeat: FullSeat
      creationDateTime: CreationDateTime
      canceledAt: CanceledAt
      refundedAt: RefundedAt
      updatedAt: UpdatedAt
      email: Email
      startDateTime: StartDateTime
      endDateTime: EndDateTime
      timeslot: TimeSlotRead {
        eventKey: EventKey
      }
      priceValue: PriceValue
      accountingValue: AccountingValue
      priceId: PriceId
      priceName: PriceName
      ProductSlotPriceRead {
        RetailOptionList {
          Id
          OptionName
          OptionType
        }
      }
      paymentFeeCollected: PaymentFeeCollected
      paymentFeeEffective: PaymentFeeEffective
      paymentFeeBilled: PaymentFeeBilled
      refundAmount: RefundAmount
      barCode: BarCode
      categoryId: CategoryId
      categoryName: CategoryName
      ProductSlotCategoryRead {
        ProductId
        Id
        Type
        TicketGroupTicketList {
          TicketGroupId
        }
        mainCategory: ProductSlotCategoryRead {
          id: Id
          type: Type
        }
        ProductRead {
          GroupRead {
            groupUuid: Uuid
          }
        }
      }
      type: Type
      promocodeDiscountValue: PromocodeDiscountValue
      timeSlotId: TimeSlotId
      sellerUserId: SellerUserId
      productOwnerGroupId: ProductOwnerGroupId
      _discountValue: DiscountValue
      sendTicket: SendTicket
      mainTicketId: MainTicketId
      mainTicketUUID: MainTicketUuid
      mainSlot: MainSlot
      packagePriceId: PackagePriceId
      PackagePriceRead {
        Id
        PriceId
        AutomaticallyBook
      }
      customFieldsAnswers: TicketFieldAnswerList {
        answer: Answer
        createdAt: CreatedAt
        CustomTicketFieldId
        customField: CustomTicketFieldRead {
          answerExample: AnswerExample
          cancelled: Canceled
          id: Id
          informationType: InformationType
          list: List
          name: Name
          position: Position
          productId: ProductId
          questionType: QuestionType
          validation: Validation
          visibleFor: VisibleFor
        }
        updatedAt: UpdatedAt
        uuid: Id
        id: Id
      }

      OrderSessionRead {
        Id
        Uid
      }
    }
  }
`;

/**
 * Count PS By BookingId
 * where: "(booking_id,in,7256454)"
 */
export const COUNT_PSSByBookingId = gql`
  query CountPSSByBookingId($id: String!) {
    countPSSByBookingId: ProductSlotStateCount(where: $id)
  }
`;

/**
 * Pull accountings of type TICKET of this booking wtih a paginated query, with their accountingEntries array
 * cond: "(booking_id,in,7256454)~and(transaction_type,eq,4)"
 */
export const GET_TicketTypeAccountingsByBookingIdWithAccEntries = gql`
  query GetTicketTypeAccountingsByBookingIdWithAccEntries(
    $cond: String!
    $skip: Int
    $limit: Int
  ) {
    GetTicketTypeAccountingsByBookingIdWithAccEntries: AccountingList(
      where: $cond
      offset: $skip
      limit: $limit
    ) {
      _id: Id
      id: Id
      uuid: Uuid
      bookingId: BookingId
      promoCodeId: PromoCodeId
      value: Value
      paymentCurrency: PaymentCurrency
      providedCashBill: ProvidedCashBill
      exchangeRate: ExchangeRate
      state: State
      transactionType: TransactionType
      paymentMethod: PaymentMethod
      createdAt: CreatedAt
      message: Message
      note: Note
      isDeposit: IsDeposit
      updatedAt: UpdatedAt
      currency: Currency
      discountId: DiscountId
      pspReference: PspReference
      refundPspReference: RefundPspReference
      cashRegisterId: CashRegisterId
      transactionId: TransactionId
      splitReference: SplitReference
      refund: Refund
      CfUid: CfUid
      channel: Channel
      OrderSessionRead {
        Id
        Uid
      }

      PromoCodeRead {
        code: Code
      }

      accountingEntriesList: AccountingEntryList {
        _id: Id
        id: Id
        uuid: Uuid
        accountingId: AccountingId
        #Missing but you can get it accountingUuid --> This is assigned a value when we convert the pulled order into an IPSB format
        #Missing productSlotStateUuid --> This is assigned a value when we convert the pulled order into an IPSB format
        productSlotStateId: ProductSlotStateId
        ProductSlotStateRead {
          Id
          Uuid
        }
        value: Value
        paymentCurrencyValue: PaymentCurrencyValue
        groupId: GroupId
        bookingStatementVersion: BookingStatementVersion
        createdAt: CreatedAt
        updatedAt: UpdatedAt
        productId: ProductId
        acompteConfigId: AcompteConfigId

        # For TAX AE
        taxId: TaxId

        # new TAX logic
        salesTaxIncluded: SalesTaxIncluded
        salesTaxRate: SalesTaxRate
        salesTaxMicroValue: SalesTaxMicroValue

        OrderSessionRead {
          Id
          Uid
        }

        OfferRead {
          Id
          Uid
        }
      }

      ProductSlotBookingRead {
        GroupRead {
          groupUuid: Uuid
        }
      }
    }
  }
`;

/**
 * Pull each accounting of type NOT TICKET (so discount and payment or any other type) of this booking
 * cond: "(booking_id,in,7256454)~and((transaction_type,lt,4)~or(transaction_type,gt,4))"
 */
export const GET_OtherTypesAccountingsByBookingId = gql`
  query GetOtherTypesAccountingsByBookingId($cond: String!) {
    getOtherTypesAccountingsByBookingId: AccountingList(where: $cond) {
      _id: Id
      id: Id
      uuid: Uuid
      bookingId: BookingId
      originalAccountingId: OriginalAccountingId # This will have a value if the current accounting is a refund of another accounting
      #Missing bookingUuid  --> We assigned booking.Uuid value mentioned above to it
      promoCodeId: PromoCodeId
      #Missing promoCodeUuid: Do we really need to un-apply a promocode? I think obviously No
      value: Value
      paymentCurrency: PaymentCurrency
      #paymentCurrencyValue: PaymentCurrencyValue
      providedCashBill: ProvidedCashBill
      exchangeRate: ExchangeRate
      state: State
      transactionType: TransactionType
      paymentMethod: PaymentMethod
      createdAt: CreatedAt
      message: Message
      note: Note
      isDeposit: IsDeposit
      updatedAt: UpdatedAt
      currency: Currency
      discountId: DiscountId
      #Missing: discountUuid: Do we really need to edit an applied discount value? I think obviously No
      pspReference: PspReference
      refundPspReference: RefundPspReference
      #Missing posPaymentMethod: This is special for new POS bookings, if the user when editing is going to pay a ticket then we will use it when creating a new accounting only
      cashRegisterId: CashRegisterId
      ## Below are split info: For the missing ones, if we don't pull them and we don't split them are they going to be updated in the backend
      #Missing transactionTimestamp
      #Missing saleId
      #Missing poiId
      #Missing accountCode
      #Missing fullTransactionId
      #Missing paymentDetails
      transactionId: TransactionId
      splitReference: SplitReference
      refund: Refund
      CfUid: CfUid
      channel: Channel
      OrderSessionRead {
        Id
        Uid
      }

      PromoCodeRead {
        code: Code
      }

      ProductSlotBookingRead {
        GroupRead {
          groupUuid: Uuid
        }
      }
    }
  }
`;

/**
 * Pull accountingEntries of accountings of GetOtherTypesAccountingsByBookingId with a paginated query
 * cond: "(accounting_id,in,1,2,3)"
 */
export const Get_AccountingEntiesByListOfAccountingIds = gql`
  query GetAccountingEntiesByListOfAccountingIds(
    $cond: String!
    $skip: Int
    $limit: Int
  ) {
    GetAccountingEntiesByListOfAccountingIds: AccountingEntryList(
      where: $cond
      offset: $skip
      limit: $limit
    ) {
      _id: Id
      id: Id
      uuid: Uuid
      accountingId: AccountingId
      productSlotStateId: ProductSlotStateId
      ProductSlotStateRead {
        Id
        Uuid
      }
      value: Value
      paymentCurrencyValue: PaymentCurrencyValue
      groupId: GroupId
      bookingStatementVersion: BookingStatementVersion
      createdAt: CreatedAt
      updatedAt: UpdatedAt
      productId: ProductId
      acompteConfigId: AcompteConfigId

      # For TAX AE
      taxId: TaxId

      # new TAX logic
      salesTaxIncluded: SalesTaxIncluded
      salesTaxRate: SalesTaxRate
      salesTaxMicroValue: SalesTaxMicroValue

      OrderSessionRead {
        Id
        Uid
      }

      OfferRead {
        Id
        Uid
      }

      GroupRead {
        groupUuid: Uuid
      }
    }
  }
`;
